import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API } from "aws-amplify";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMModels } from "../dataaccess/fetchLLMModels";
import { useUser } from "../contexts/useUser";
import { apiKeyManagementHeaders } from "../utils/apiKeyManagementAuth";
import {
  GatewayBatchResponse,
  VendorBatchCall,
  batchCallsComplete,
  buildVendorBatchPayload,
  createIdempotencyKey,
  isTerminalBatchCall,
  normalizeGatewayBaseUrl,
  parseBatchPromptLines,
} from "./vendorBatchTestUtils";

const DEV_GATEWAY_FUNCTION_URL =
  "https://o3lzqsgeppodhkif7jdgxzykhi0uxcus.lambda-url.us-east-1.on.aws";

const defaultGatewayUrl = () => {
  const configuredUrl = process.env.REACT_APP_LLM_GATEWAY_URL?.trim();
  if (configuredUrl) return configuredUrl;

  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    return DEV_GATEWAY_FUNCTION_URL;
  }
  return "https://gateway.llmasaservice.io";
};

const statusLabel = (status?: string | number) => {
  const value = status === undefined ? "" : String(status);
  if (value === "202") return "Pending";
  if (value === "200") return "Completed";
  if (value === "402") return "Usage denied";
  if (value === "403") return "Customer denied";
  if (value) return `Failed (${value})`;
  return "Waiting";
};

const statusColor = (status?: string | number) => {
  const value = status === undefined ? "" : String(status);
  if (value === "200") return "success" as const;
  if (value === "202") return "warning" as const;
  if (value) return "error" as const;
  return "default" as const;
};

const formatTimestamp = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const extractFetchError = async (response: Response) => {
  const text = await response.text();
  try {
    const body = JSON.parse(text);
    return body?.error?.message || body?.message || text;
  } catch {
    return text || `HTTP ${response.status}`;
  }
};

const BatchChatTest: React.FC = () => {
  const { project, user } = useUser();
  const [gatewayUrl, setGatewayUrl] = useState(defaultGatewayUrl);
  const [apiKey, setApiKey] = useState("");
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [customerId, setCustomerId] = useState(user?.email || "admin-batch-test");
  const [promptsText, setPromptsText] = useState(
    "Reply with exactly: vendor batch item one\nReply with exactly: vendor batch item two"
  );
  const [maxCompletionTokens, setMaxCompletionTokens] = useState(64);
  const [callbackUrl, setCallbackUrl] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey);
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [autoPoll, setAutoPoll] = useState(true);
  const [submitError, setSubmitError] = useState("");
  const [pollError, setPollError] = useState("");
  const [batch, setBatch] = useState<GatewayBatchResponse | null>(null);
  const [calls, setCalls] = useState<VendorBatchCall[]>([]);
  const [lastPolledAt, setLastPolledAt] = useState("");
  const [lastRequest, setLastRequest] = useState<Record<string, unknown> | null>(null);
  const [showCallbackSecret, setShowCallbackSecret] = useState(false);

  const { data: modelsData, isLoading: modelsLoading, isError: modelsError } = useQuery({
    queryKey: ["models", "vendor-batch-test"],
    queryFn: () => fetchLLMModels(),
  });

  const batchModels = useMemo(() => {
    const models = modelsData?.data?.listLLMModels?.items ?? [];
    return models
      .filter((model) => {
        const host = String(model?.host || model?.vendor || "").toLowerCase();
        return (
          model?.id &&
          ["openai", "anthropic"].includes(host) &&
          Number(model?.inputTokenBatchPrice || 0) > 0 &&
          Number(model?.outputTokenBatchPrice || 0) > 0
        );
      })
      .sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || "")));
  }, [modelsData]);

  useEffect(() => {
    if (!selectedModel && batchModels[0]?.id) {
      setSelectedModel(`model:${batchModels[0].id}`);
    }
  }, [batchModels, selectedModel]);

  useEffect(() => {
    if (user?.email) setCustomerId(user.email.toLowerCase());
  }, [user?.email]);

  useEffect(() => {
    const loadApiKeys = async () => {
      if (!project?.id) return;
      setApiKeyLoading(true);
      setApiKeyError("");
      try {
        const response = await API.get("eventapi", "/getIntegrationApiKeys", {
          headers: await apiKeyManagementHeaders(),
          queryStringParameters: { projectId: project.id },
        });
        if (!response?.success) throw new Error("Unable to load integration API key status");
        const keys = JSON.parse(response.body);
        const configured = Boolean(
          keys.key1Configured || keys.key2Configured || keys.key1 || keys.key2
        );
        setApiKeyConfigured(configured);
        setApiKey(keys.key1 || keys.key2 || "");
        if (configured && !keys.key1 && !keys.key2) {
          setApiKeyError(
            "A key is configured, but plaintext keys are not stored. Paste an integration API key below."
          );
        } else if (!configured) {
          setApiKeyError("No integration API key is configured for this project.");
        }
      } catch (error: any) {
        setApiKeyError(error?.message || "Unable to load integration API keys.");
      } finally {
        setApiKeyLoading(false);
      }
    };
    loadApiKeys();
  }, [project?.id]);

  const createApiKeys = async () => {
    if (!project?.id || !project?.name) return;
    setApiKeyLoading(true);
    setApiKeyError("");
    try {
      const response = await API.post("eventapi", "/createIntegrationApiKeys", {
        headers: await apiKeyManagementHeaders(),
        body: { projectId: project.id, projectName: project.name },
      });
      if (!response?.success) throw new Error("Unable to create integration API keys");
      const keys = JSON.parse(response.body);
      setApiKeyConfigured(true);
      setApiKey(keys.key1 || keys.key2 || "");
    } catch (error: any) {
      setApiKeyError(error?.message || "Unable to create integration API keys.");
    } finally {
      setApiKeyLoading(false);
    }
  };

  const promptLines = useMemo(() => parseBatchPromptLines(promptsText), [promptsText]);
  const expectedCalls = batch?.request_counts?.total || promptLines.length;
  const complete = batchCallsComplete(calls, expectedCalls);

  const pollBatch = useCallback(
    async (batchId?: string) => {
      const activeBatchId = batchId || batch?.id;
      if (!activeBatchId || !project?.id || !apiKey) return;
      setPolling(true);
      setPollError("");
      try {
        const response = await API.get(
          "llmpublicapi",
          `/calls?batch_id=${encodeURIComponent(activeBatchId)}&page_size=100`,
          {
            headers: {
              "x-api-key": apiKey,
              "x-project-id": project.id,
            },
          }
        );
        setCalls(Array.isArray(response) ? response : []);
        setLastPolledAt(new Date().toISOString());
      } catch (error: any) {
        setPollError(error?.message || "Unable to poll the batch call records.");
      } finally {
        setPolling(false);
      }
    },
    [apiKey, batch?.id, project?.id]
  );

  useEffect(() => {
    if (!batch?.id || !apiKey || !autoPoll || complete) return;
    const timer = window.setInterval(() => pollBatch(batch.id), 10000);
    return () => window.clearInterval(timer);
  }, [apiKey, autoPoll, batch?.id, complete, pollBatch]);

  const submitBatch = async () => {
    if (!project?.id) {
      setSubmitError("Select a project before submitting a batch.");
      return;
    }
    if (!apiKey.trim()) {
      setSubmitError("Paste a project integration API key first.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    setPollError("");
    setBatch(null);
    setCalls([]);
    setLastPolledAt("");
    setShowCallbackSecret(false);

    try {
      const body = buildVendorBatchPayload({
        prompts: promptLines,
        model: selectedModel,
        customerId: customerId.trim() || "admin-batch-test",
        callbackUrl,
        maxCompletionTokens,
      });
      setLastRequest(body);
      const response = await fetch(`${normalizeGatewayBaseUrl(gatewayUrl)}/v1/batches`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey.trim()}`,
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
          "x-project-id": project.id,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(await extractFetchError(response));
      const result = (await response.json()) as GatewayBatchResponse;
      setBatch(result);
      setCalls(
        (result.calls || []).map((call) => ({
          id: call.call_id,
          batchId: result.id,
          batchCustomId: call.custom_id,
          status: call.status,
        }))
      );
      window.setTimeout(() => pollBatch(result.id), 1500);
    } catch (error: any) {
      const message = error?.message || "Unable to submit the vendor batch.";
      setSubmitError(
        message === "Failed to fetch"
          ? `The browser could not reach ${normalizeGatewayBaseUrl(gatewayUrl)}. Check the Function URL and its CORS configuration.`
          : message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      setSubmitError("Clipboard access is unavailable. Select and copy the value manually.");
    }
  };

  if (modelsLoading) return <LinearProgress />;
  if (modelsError) return <Alert severity="error">Unable to load models.</Alert>;

  return (
    <Stack spacing={2}>
      <Paper elevation={3} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Box p={3}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h5">True Vendor Batch Test</Typography>
              <Typography variant="body2" color="text.secondary">
                Sends one or more requests directly to LLMGateway, then polls the public calls API.
                This does not use the chat Lambda or its legacy background mode.
              </Typography>
            </Box>
            <Chip label="OpenAI + Anthropic Batch APIs" color="primary" variant="outlined" />
          </Stack>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                size="small"
                label="LLMGateway base URL"
                value={gatewayUrl}
                onChange={(event) => setGatewayUrl(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel id="vendor-batch-model-label">Model or group</InputLabel>
                <Select
                  labelId="vendor-batch-model-label"
                  label="Model or group"
                  value={selectedModel}
                  onChange={(event) => setSelectedModel(event.target.value)}
                >
                  <MenuItem value="group:standard">
                    Standard group (auto-select)
                  </MenuItem>
                  <MenuItem value="group:premium">
                    Premium group (auto-select)
                  </MenuItem>
                  {batchModels.map((model) => (
                    <MenuItem key={model?.id || ""} value={`model:${model?.id}`}>
                      {model?.name || model?.modelKeyLatest || model?.modelKey || model?.id}
                      {" · "}
                      {model?.host || model?.vendor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                type="password"
                size="small"
                autoComplete="off"
                label="Project integration API key"
                value={apiKey}
                onChange={(event) => {
                  setApiKey(event.target.value);
                  if (event.target.value) setApiKeyError("");
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Customer ID"
                value={customerId}
                onChange={(event) => setCustomerId(event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Callback URL (optional)"
                placeholder="https://example.com/webhooks/llm-batch"
                value={callbackUrl}
                onChange={(event) => setCallbackUrl(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max completion tokens"
                value={maxCompletionTokens}
                inputProps={{ min: 1, max: 4096 }}
                onChange={(event) => setMaxCompletionTokens(Math.max(1, Number(event.target.value) || 1))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label={`Prompts — one request per non-empty line (${promptLines.length})`}
                value={promptsText}
                onChange={(event) => setPromptsText(event.target.value)}
                helperText="Each line becomes an independent Batch API request with custom IDs admin-001, admin-002, and so on."
              />
            </Grid>

            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                size="small"
                label="Idempotency-Key"
                value={idempotencyKey}
                onChange={(event) => setIdempotencyKey(event.target.value)}
                helperText="Keep this value to retry the same submission safely. Generate a new value for a new batch."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth variant="outlined" onClick={() => setIdempotencyKey(createIdempotencyKey())}>
                New key
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Button
                  variant="contained"
                  onClick={submitBatch}
                  disabled={submitting || !project?.id || !selectedModel || promptLines.length === 0}
                >
                  Submit Vendor Batch
                </Button>
                <FormControlLabel
                  control={<Switch checked={autoPoll} onChange={(event) => setAutoPoll(event.target.checked)} />}
                  label="Poll every 10 seconds"
                />
                {batch?.id ? (
                  <Button variant="outlined" onClick={() => pollBatch()} disabled={polling || !apiKey}>
                    Poll now
                  </Button>
                ) : null}
              </Stack>
            </Grid>
          </Grid>

          {submitting || polling || apiKeyLoading ? <LinearProgress sx={{ mt: 2 }} /> : null}
          {apiKeyError ? (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={!apiKeyConfigured ? (
                <Button color="inherit" size="small" onClick={createApiKeys}>Create keys</Button>
              ) : undefined}
            >
              {apiKeyError}
            </Alert>
          ) : null}
          {batchModels.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No active OpenAI or Anthropic models with positive input and output batch pricing are available.
            </Alert>
          ) : null}
          {submitError ? <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert> : null}
          {pollError ? <Alert severity="warning" sx={{ mt: 2 }}>{pollError}</Alert> : null}
        </Box>
      </Paper>

      {batch ? (
        <Paper elevation={3} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
          <Box p={3}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h6">Batch {batch.id}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Accepted {formatTimestamp(batch.created_at)} · Last poll {formatTimestamp(lastPolledAt)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={complete ? "All calls terminal" : batch.status} color={complete ? "success" : "warning"} />
                <Chip label={`${calls.filter(isTerminalBatchCall).length}/${expectedCalls} terminal`} variant="outlined" />
                <Button size="small" onClick={() => copyText(batch.id)}>Copy batch ID</Button>
              </Stack>
            </Stack>

            {batch.callback_secret ? (
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  type={showCallbackSecret ? "text" : "password"}
                  label="Callback signing secret — returned once"
                  value={batch.callback_secret}
                  InputProps={{ readOnly: true }}
                />
                <Button variant="outlined" onClick={() => setShowCallbackSecret((value) => !value)}>
                  {showCallbackSecret ? "Hide" : "Show"}
                </Button>
                <Button variant="outlined" onClick={() => copyText(batch.callback_secret || "")}>Copy</Button>
              </Stack>
            ) : null}
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Custom ID</TableCell>
                  <TableCell>Call ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Response / Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.map((call, index) => (
                  <TableRow key={call.id || `${call.batchCustomId}-${index}`}>
                    <TableCell>{call.batchCustomId || batch.calls[index]?.custom_id || "-"}</TableCell>
                    <TableCell>
                      <Tooltip title={call.id || ""}>
                        <Button size="small" onClick={() => call.id && copyText(call.id)}>
                          {call.id ? `${call.id.slice(0, 12)}…` : "-"}
                        </Button>
                      </Tooltip>
                    </TableCell>
                    <TableCell><Chip size="small" label={statusLabel(call.status)} color={statusColor(call.status)} /></TableCell>
                    <TableCell>{call.vendorStatus || "queued"}</TableCell>
                    <TableCell>{call.tokenCountPrompt || 0} / {call.tokenCountResponse || 0}</TableCell>
                    <TableCell>{call.cost === undefined ? "-" : call.cost}</TableCell>
                    <TableCell sx={{ minWidth: 260, maxWidth: 520, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {call.error || call.response || (String(call.status) === "202" ? "Waiting for vendor result" : "-")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box p={3}>
            <Typography variant="subtitle2" gutterBottom>Raw submission and response</Typography>
            <Box
              component="pre"
              sx={{ m: 0, p: 2, overflow: "auto", maxHeight: 420, border: "1px solid", borderColor: "divider", borderRadius: 2, fontSize: 12 }}
            >
              {JSON.stringify({ request: lastRequest, submission: batch, calls }, null, 2)}
            </Box>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info">
          Submit a batch to see the gateway acceptance response and live call records. Vendor batches can take up to 24 hours; this page can be left open or revisited with the batch ID through the public API.
        </Alert>
      )}
    </Stack>
  );
};

export default BatchChatTest;
